// @tier: PREMIUM
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Bell, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import unifiedApi from '../services/unifiedApi';
import logger from '@/utils/logger';

export function getNotificationType(notificationType) {
  if (notificationType.includes('emergency') || notificationType.includes('alert')) return 'alert';
  if (notificationType.includes('delayed') || notificationType.includes('late')) return 'warning';
  if (notificationType.includes('completed') || notificationType.includes('success')) return 'success';
  return 'info';
}

export function getNotificationIcon(type) {
  if (type === 'alert' || type === 'warning') return AlertTriangle;
  if (type === 'success') return CheckCircle;
  if (type === 'info') return Info;
  return Bell;
}

export function getNotificationColor(type) {
  if (type === 'alert') return 'text-red-600 bg-red-50 border-red-200';
  if (type === 'warning') return 'text-orange-600 bg-orange-50 border-orange-200';
  if (type === 'success') return 'text-green-600 bg-green-50 border-green-200';
  if (type === 'info') return 'text-blue-600 bg-blue-50 border-blue-200';
  return 'text-gray-600 bg-gray-50 border-gray-200';
}

export function formatTimestamp(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (minutes < 60) return `Il y a ${minutes} min`;
  if (hours < 24) return `Il y a ${hours}h`;
  return `Il y a ${days}j`;
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filter, setFilter] = useState('all');
  const [recipientTypeFilter, setRecipientTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNotifications, setSelectedNotifications] = useState([]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { limit: 100, offset: 0 };
      if (recipientTypeFilter !== 'all') params.recipient_type = recipientTypeFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      const r = await unifiedApi.notifications.getAll(params);
      if (r.success) setNotifications(r.data.notifications);
    } catch (err) {
      logger.error('Erreur lors du chargement des notifications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const r = await unifiedApi.notifications.getStatistics();
      if (r.success) setStatistics(r.data);
    } catch (err) {
      logger.error('Erreur lors du chargement des statistiques:', err);
    }
  };

  useEffect(() => {
    loadNotifications();
    loadStatistics();
  }, [recipientTypeFilter, priorityFilter]);

  const filteredNotifications = notifications.filter((n) => {
    const isRead = n.read_at !== null;
    const matchesFilter = filter === 'all' || (filter === 'unread' && !isRead) || (filter === 'read' && isRead);
    const matchesSearch =
      searchTerm === '' ||
      n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      n.message.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = statistics || {
    total: notifications.length,
    byStatus: { read: 0, delivered: 0, sent: 0, pending: 0, failed: 0 },
    byPriority: { urgent: 0, high: 0, normal: 0, low: 0 },
    byRecipientType: { parent: 0, driver: 0, admin: 0 },
    readRate: 0,
    today: 0,
    thisWeek: 0,
  };

  const deleteNotification = async (id) => {
    try {
      await unifiedApi.notifications.delete(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      logger.error('Erreur lors de la suppression:', err);
      toast.error('Erreur lors de la suppression de la notification');
    }
  };

  const markAsRead = async (id) => {
    try {
      const r = await unifiedApi.notifications.markAsRead(id);
      if (!r.success) {
        toast.error(r.message || 'Erreur lors du marquage comme lue');
        return;
      }
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: r.data.read_at } : n)));
      loadStatistics();
    } catch (err) {
      logger.error('Erreur lors du marquage comme lue:', err);
      toast.error('Erreur de connexion — réessayez');
    }
  };

  const markAllAsRead = async () => {
    try {
      const r = await unifiedApi.notifications.markAllAsRead();
      if (!r.success) {
        toast.error(r.message || 'Erreur lors du marquage global');
        return;
      }
      const now = new Date().toISOString();
      setNotifications((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));
      toast.success(
        r.data.updated > 0
          ? `${r.data.updated} notification(s) marquée(s) comme lue(s)`
          : 'Aucune notification à marquer',
      );
      loadStatistics();
    } catch (err) {
      logger.error('Erreur lors du marquage global:', err);
      toast.error('Erreur de connexion — réessayez');
    }
  };

  const deleteSelected = async () => {
    try {
      await Promise.all(selectedNotifications.map((id) => unifiedApi.notifications.delete(id)));
      setNotifications((prev) => prev.filter((n) => !selectedNotifications.includes(n.id)));
      setSelectedNotifications([]);
    } catch (err) {
      logger.error('Erreur lors de la suppression:', err);
      toast.error('Erreur lors de la suppression des notifications');
    }
  };

  const toggleSelection = (id) => {
    setSelectedNotifications((prev) => (prev.includes(id) ? prev.filter((nId) => nId !== id) : [...prev, id]));
  };

  const selectAll = () => setSelectedNotifications(filteredNotifications.map((n) => n.id));
  const deselectAll = () => setSelectedNotifications([]);

  const unreadCount = notifications.filter((n) => n.read_at === null).length;

  return {
    notifications,
    filteredNotifications,
    statistics,
    stats,
    unreadCount,
    loading,
    error,
    filter,
    setFilter,
    recipientTypeFilter,
    setRecipientTypeFilter,
    priorityFilter,
    setPriorityFilter,
    searchTerm,
    setSearchTerm,
    selectedNotifications,
    loadNotifications,
    loadStatistics,
    deleteNotification,
    deleteSelected,
    markAsRead,
    markAllAsRead,
    toggleSelection,
    selectAll,
    deselectAll,
  };
};

export default useNotifications;
